'use client';

import * as React from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import ThemeSwitcher from '@/components/ThemeSwitcher';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { AppShell, type Role, type Tenant, type SessionUser } from '@/components/layout/app-sidebar';

import { errorMessage } from '@/lib/errors';
const API_BASE = process.env.NEXT_PUBLIC_API_BASE;

type Membership = {
  tenantId: number;
  tenantSlug: string;
  tenantName: string;
  role: string;
};

const asRole = (raw: unknown): Role =>
  raw === 'OPERATOR' || raw === 'ADMIN' ? raw : 'USER';

/** "Alpha Institute" -> "AI"; single-word names take the first two letters. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * Adapts the app's auth context to the props AppShell expects. The design
 * treats the session as plain data; everything role-dependent is derived here
 * so the shell stays presentational.
 */
export default function SessionShell({ children }: { children: React.ReactNode }) {
  const { user, logout, switchTenant } = useAuth();
  const [memberships, setMemberships] = React.useState<Membership[]>([]);

  React.useEffect(() => {
    if (!user || !API_BASE) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/users/memberships`);
        if (!res.ok) return;
        const data = (await res.json()) as Membership[] | null;
        if (!cancelled && Array.isArray(data)) setMemberships(data);
      } catch {
        // The switcher degrades to the current tenant only.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const role = asRole(user?.role);

  const tenantName = user?.tenantName ?? 'Platform';
  const tenant: Tenant = {
    id: user?.tenantSlug ?? '',
    name: tenantName,
    initials: initialsOf(tenantName),
    role,
    tint: user?.tintElements ?? undefined,
  };

  const tenants: Tenant[] = memberships.map((m) => ({
    id: m.tenantSlug,
    name: m.tenantName,
    initials: initialsOf(m.tenantName),
    role: asRole(m.role),
  }));

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'Account';

  const sessionUser: SessionUser = {
    name: displayName,
    email: user?.email ?? '',
    initials: initialsOf(displayName),
  };

  const handleSwitchTenant = async (slug: string) => {
    if (slug === (user?.tenantSlug ?? '')) return;
    try {
      await switchTenant(slug);
      toast.success('Tenant switched');
    } catch (err) {
      console.error('[SessionShell] Failed to switch tenant', err);
      toast.error(errorMessage(err) || 'Unable to switch tenant');
    }
  };

  return (
    <AppShell
      role={role}
      tenant={tenant}
      tenants={tenants}
      user={sessionUser}
      onSwitchTenant={handleSwitchTenant}
      onSignOut={logout}
      themeSlot={<ThemeSwitcher />}
    >
      {children}
    </AppShell>
  );
}
